// ==============================
// Imports
// ==============================

// MUI Components
import {
  Edit,
  Search,
  Receipt,
  Delete,
  KeyboardArrowUp,
} from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  // Avatar,
  Stack,
  IconButton,
  Typography,
  Box,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  // Divider,
  Tooltip,
  // TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Fab,
} from '@mui/material';
import { TableNoData, TableSpacerRow } from './common/TableEmptyState';
import TableLoader from './common/TableLoader';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';

// Libraries
import { useState } from 'react';
import { Download, Send } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';

// Local Components
import {
  serviceColors,
  serviceTypeColors,
  // userTypeColors,
} from '../utils/colorHandler';
import { notifyAPIClient } from '../utils/toast';

// Hook
import { useRegistrationController } from '../hooks/useRegistrationTable';
import {
  ServiceType,
  Subsubtype,
  Subtype,
} from '../types/ServiceAccordion.type';
import EditRegistrationDialog from './FormDialog/EditRegistrationDialog';
import RegistrationConfirmationDialog from './FormDialog/RegistrationConfirmationDialog';
import ColorChip from './common/ColorChip';
import GenderChip from './common/GenderChip';
import {
  rowHoverBg,
  selectedRowBg,
  selectedRowBgOpaque,
  walkInRowBg,
  walkInRowBgOpaque,
} from '../utils/rowHighlight';
import { useTranslation } from '../i18n';

const RegistrationTable = () => {
  const { t } = useTranslation();
  const {
    registrations,
    page,
    limit,
    totalItems,
    searchTerm,
    serviceTypeFilter,
    subTypeFilter,
    subSubTypeFilter,
    statusFilter,
    setStatusFilter,
    anonymousFilter,
    setAnonymousFilter,
    paymentMethodFilter,
    setPaymentMethodFilter,
    invoicedFilter,
    setInvoicedFilter,
    emptyRows,
    // calculateAge,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleFilterChange,
    handleFilterSubtypeChange,
    handleFilterSubSubtypeChange,
    clearFilters,

    fetchRegistrations,
    setPage,
    serviceOptions,
    openRegistrationInfoDialog,
    setOpenRegistrationInfoDialog,
    selectedRegistration,
    setSelectedRegistration,
    handleCloseRegistrationInfoDialog,

    confirmInvoiceDialogOpen,
    setConfirmInvoiceDialogOpen,
    handlePrintInvoice,
    handlePrintMultipleInvoices,
    createInvoiceLoading,

    handleDeleteRegistration,
    openDeleteRegistrationDialog,
    setOpenDeleteRegistrationDialog,

    selectedRegistrations,
    setSelectedRegistrations,
    handleSelectRegistration,
    selectedUserId,
    setSelectedUserId,

    lastSyncTime,
    highlightedRow,
    setHighlightedRow,

    loading,

    handleDownloadRegistrationList,
    downloadLoading,

    filterMonth,
    setFilterMonth,
    filterYear,
    setFilterYear,
  } = useRegistrationController();

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationMode, setConfirmationMode] = useState<'send' | 'download'>(
    'download',
  );

  const openConfirmationDialog = (
    registration: (typeof registrations)[number],
    mode: 'send' | 'download',
  ) => {
    setSelectedRegistration(registration);
    setConfirmationMode(mode);
    setConfirmationDialogOpen(true);
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialogOpen(false);
    setSelectedRegistration(null);
  };

  // Registrations whose confirmation PDF has already been downloaded — the
  // download button turns green so admins can tell at a glance which are done.
  // Persisted in localStorage so the state survives reloads.
  const DOWNLOADED_CONFIRMATIONS_KEY = 'downloaded_confirmations';
  const [downloadedConfirmations, setDownloadedConfirmations] = useState<
    Set<number>
  >(() => {
    try {
      const raw = localStorage.getItem(DOWNLOADED_CONFIRMATIONS_KEY);
      return new Set<number>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<number>();
    }
  });

  const markConfirmationDownloaded = (registrationId: number) => {
    setDownloadedConfirmations((prev) => {
      const next = new Set(prev);
      next.add(registrationId);
      try {
        localStorage.setItem(
          DOWNLOADED_CONFIRMATIONS_KEY,
          JSON.stringify(Array.from(next)),
        );
      } catch {
        // Ignore storage failures — the in-memory state still updates.
      }
      return next;
    });
  };

  // ==============================
  // Render
  // ==============================
  return (
    <Paper>
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        sx={{ flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          placeholder={t('registration.searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              fetchRegistrations();
              setPage(1);
              // notifyActionSuccess('Search');
            }
          }}
          sx={{ width: 350 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />

        <Box
          display={'flex'}
          sx={{ gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={serviceTypeFilter?.id ?? ''}
              onChange={handleFilterChange}
            >
              <MenuItem value="-1">{t('registration.allProducts')}</MenuItem>
              {serviceOptions.map((option: ServiceType) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={subTypeFilter?.id ?? ''}
              onChange={handleFilterSubtypeChange}
            >
              <MenuItem value="-1">
                {t('registration.allServiceTypes')}
              </MenuItem>
              {serviceTypeFilter?.subtypes.map((option: Subtype) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={subSubTypeFilter?.id ?? ''}
              onChange={handleFilterSubSubtypeChange}
            >
              <MenuItem value="-1">{t('registration.allPackages')}</MenuItem>
              {subTypeFilter?.subsubtypes.map((option: Subsubtype) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <MenuItem value="all">{t('registration.allStatus')}</MenuItem>
              <MenuItem value="active">{t('registration.active')}</MenuItem>
              <MenuItem value="inactive">{t('registration.inactive')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={anonymousFilter}
              onChange={(event) => setAnonymousFilter(event.target.value)}
            >
              <MenuItem value="all">{t('registration.allCustomers')}</MenuItem>
              <MenuItem value="anonymous">
                {t('registration.anonymousOnly')}
              </MenuItem>
              <MenuItem value="non_anonymous">
                {t('registration.nonAnonymousOnly')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={paymentMethodFilter}
              onChange={(event) => setPaymentMethodFilter(event.target.value)}
            >
              <MenuItem value="all">
                {t('registration.allPaymentMethods')}
              </MenuItem>
              <MenuItem value="CASH">{t('registration.cash')}</MenuItem>
              <MenuItem value="BANK_TRANSFER">
                {t('registration.bankTransfer')}
              </MenuItem>
              <MenuItem value="DEPOSIT">{t('registration.deposit')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={invoicedFilter}
              onChange={(event) => setInvoicedFilter(event.target.value)}
            >
              <MenuItem value="all">{t('registration.allInvoices')}</MenuItem>
              <MenuItem value="invoiced">{t('registration.invoiced')}</MenuItem>
              <MenuItem value="not_invoiced">
                {t('registration.notInvoiced')}
              </MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={['month', 'year']}
              label={t('registration.filterByMonth')}
              value={
                filterMonth && filterYear
                  ? dayjs(
                      `${filterYear}-${String(filterMonth).padStart(2, '0')}-01`,
                    )
                  : null
              }
              onChange={(newValue: Dayjs | null) => {
                if (newValue) {
                  setFilterMonth(newValue.month() + 1);
                  setFilterYear(newValue.year());
                } else {
                  setFilterMonth(null);
                  setFilterYear(null);
                }
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 200 },
                },
                field: {
                  clearable: true,
                  onClear: () => {
                    setFilterMonth(null);
                    setFilterYear(null);
                  },
                },
              }}
            />
          </LocalizationProvider>

          {/* Clear filters button */}
          {searchTerm ||
          serviceTypeFilter?.id !== '-1' ||
          statusFilter !== 'all' ||
          anonymousFilter !== 'all' ||
          paymentMethodFilter !== 'all' ||
          invoicedFilter !== 'all' ||
          filterMonth !== null ||
          filterYear !== null ? (
            <Button
              size="small"
              variant="contained"
              color="info"
              onClick={clearFilters}
              startIcon={<FilterAltOffIcon />}
            >
              {t('registration.clear')}
            </Button>
          ) : null}

          <Tooltip title={t('registration.downloadRegistrationList')}>
            <IconButton
              onClick={handleDownloadRegistrationList}
              loading={downloadLoading}
              disabled={
                registrations.length > 0 && !downloadLoading ? false : true
              }
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <TableContainer>
        <Table size="small" padding="checkbox">
          {/* ********** */}
          {/* HEADER */}
          {/* ********** */}
          <TableHead>
            {/* <TableRow sx={{ backgroundColor: '#4da7db' }}>
              <TableCell
                colSpan={6}
                sx={{ fontWeight: 'bold', color: '#fff', textAlign: 'center' }}
              >
                <Typography fontStyle="italic" color="#fff">
                  User Information
                </Typography>
              </TableCell>
              <TableCell
                colSpan={7}
                sx={{ fontWeight: 'bold', color: '#fff', textAlign: 'center' }}
              >
                <Typography fontStyle="italic" color="#fff">
                  Service Information
                </Typography>
              </TableCell>
            </TableRow> */}

            <TableRow sx={{ backgroundColor: '#4da7db' }}>
              {/* User Sub-Headers */}
              <TableCell padding="checkbox">
                <Tooltip title={t('registration.clearSelection')}>
                  <IconButton
                    color="default"
                    aria-label="clear selection"
                    disabled={selectedRegistrations.length === 0}
                    onClick={() => {
                      setSelectedUserId(null);
                      setSelectedRegistrations([]);
                    }}
                  >
                    {selectedRegistrations.length > 0 ? (
                      <IndeterminateCheckBoxIcon />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colUserId')}
                </Typography>
              </TableCell>
              {/* <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  Avatar
                </Typography>
              </TableCell> */}
              {/* <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  Full Name (Preferred Name)
                </Typography>
              </TableCell> */}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colLastName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colFirstName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colPreferredName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colBusinessName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colDateOfBirth')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colAge')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colGender')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colParentName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colEmail')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colPhone')}
                </Typography>
              </TableCell>
              {/* <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  Parent / Contact
                </Typography>
              </TableCell> */}
              {/* <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  Type / Date of Birth
                </Typography>
              </TableCell> */}

              {/* Service Sub-Headers */}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colProduct')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colType')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colDetails')}
                </Typography>
              </TableCell>
              {/* <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  Price
                </Typography>
              </TableCell> */}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colTotalCost')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colDiscountAmount')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colQuantity')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colStartDate')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colEndDate')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colPaymentMethod')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('registration.colInvoiced')}
                </Typography>
              </TableCell>
              {/* <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  Start / End / Duration
                </Typography>
              </TableCell> */}
              <TableCell
                sx={{
                  whiteSpace: 'nowrap',
                  position: 'sticky',
                  right: -16,
                  zIndex: '1 !important',
                }}
              >
                <Typography
                  fontWeight="bold"
                  color="#fff"
                  bgcolor={'#4da7db'}
                  borderLeft={1}
                  textAlign="center"
                  className="!px-2"
                >
                  {t('registration.colActions')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          {/* ********** */}
          {/* DATA MANIPULATION */}
          {/* ********** */}
          <TableBody>
            {loading ? (
              <TableLoader colSpan={23} />
            ) : (
              <>
                {registrations.map((registrations) => {
                  // Anonymous walk-in customer (no full info collected) — rendered
              // with a distinct, muted appearance so it stands out from real
              // customer records.
              const isAnonymous = registrations.userId === 999999;
              return (
                <TableRow
                  key={registrations.id}
                  sx={{
                    '&:hover': { backgroundColor: rowHoverBg },
                    backgroundColor:
                      highlightedRow === `data_${registrations.id}`
                        ? selectedRowBg
                        : isAnonymous
                          ? walkInRowBg
                          : 'inherit',
                    ...(isAnonymous && {
                      fontStyle: 'italic',
                      color: 'text.secondary',
                    }),
                  }}
                  onClick={() => {
                    if (highlightedRow === `data_${registrations.id}`) {
                      setHighlightedRow(null);
                    } else {
                      setHighlightedRow(`data_${registrations.id}`);
                    }
                  }}
                >
                  {/* User information */}
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={selectedRegistrations.includes(registrations.id)}
                      disabled={
                        registrations.isInvoiced ||
                        (selectedUserId !== null &&
                          selectedUserId !== registrations.user.id)
                      }
                      onChange={() => {
                        if (!selectedUserId)
                          setSelectedUserId(registrations.user.id);

                        handleSelectRegistration(registrations);
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      ...(isAnonymous && {
                        borderLeft: '3px solid #f0ad4e',
                      }),
                    }}
                  >
                    {isAnonymous ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{registrations.userId}</span>
                        <Chip
                          label={t('registration.anonymous')}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Stack>
                    ) : (
                      registrations.userId
                    )}
                  </TableCell>
                  {/* <TableCell>
                  <Avatar src={registrations.user.pictureUrl} />
                </TableCell> */}
                  {/* <TableCell>{`${registrations.user.firstName} ${registrations.user.lastName} (${registrations.user.preferredName || 'None'})`}</TableCell> */}
                  <TableCell
                    sx={{ whiteSpace: 'nowrap' }}
                  >{`${registrations.user.lastName}`}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{`${
                    registrations.userId === 999999 &&
                    registrations.walkInCustomerName
                      ? registrations.walkInCustomerName
                      : registrations.user.firstName
                  }`}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {registrations.user.preferredName || (
                      <span style={{ opacity: 0.4 }}>
                        {t('registration.none')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {registrations.user.businessName || (
                      <span style={{ opacity: 0.4 }}>
                        {t('registration.none')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {registrations.user.userType === 'BUSINESS'
                      ? '---'
                      : new Date(registrations.user.dob).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {registrations.user.userType === 'BUSINESS'
                      ? '---'
                      : Math.floor(
                          (new Date().getTime() -
                            new Date(registrations.user.dob).getTime()) /
                            (365.25 * 24 * 60 * 60 * 1000),
                        )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <GenderChip value={registrations.user.gender} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const parent = registrations.user.parent;
                      // Treat empty AND the literal "None" string as no value.
                      const isEmpty =
                        !parent || parent.trim().toLowerCase() === 'none';
                      return isEmpty ? (
                        <span style={{ opacity: 0.4 }}>
                          {t('registration.none')}
                        </span>
                      ) : (
                        parent
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const email = registrations.user.email;
                      // No email on file — fade the placeholder.
                      if (!email)
                        return (
                          <span style={{ opacity: 0.4 }}>
                            {t('registration.none')}
                          </span>
                        );
                      if (isAnonymous) return email;
                      // Real email — underline it and let admins click through to
                      // compose a message in Gmail.
                      return (
                        <Tooltip title={t('registration.clickToOpenGmail')}>
                          <a
                            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              textDecoration: 'underline',
                              color: 'inherit',
                            }}
                          >
                            {email}
                          </a>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const phone = registrations.user.phone;
                      // Placeholder number used when a real phone wasn't collected.
                      const PLACEHOLDER_PHONE = '0909999999';
                      if (!phone)
                        return (
                          <span style={{ opacity: 0.4 }}>
                            {t('registration.none')}
                          </span>
                        );
                      if (phone === PLACEHOLDER_PHONE) {
                        // Faded: this is a placeholder, not a real number.
                        return <span style={{ opacity: 0.4 }}>{phone}</span>;
                      }
                      // Real number provided by the customer — underline it and
                      // let admins click through to the customer's Zalo.
                      return (
                        <Tooltip title={t('registration.clickToOpenZalo')}>
                          <a
                            href={`https://zalo.me/${phone}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              textDecoration: 'underline',
                              color: 'inherit',
                            }}
                          >
                            {phone}
                          </a>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>

                  {/* Service information */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ColorChip
                      label={registrations.serviceType}
                      palette={serviceColors}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ColorChip
                      label={registrations.subtype}
                      palette={serviceTypeColors}
                    />
                    {/* <div
                    style={{
                      fontSize: '1.2em',
                      color: serviceColors[registrations.serviceType],
                    }}
                  >
                    {registrations.subsubtype.name || '---'}
                  </div>
                  <div
                    style={{
                      fontSize: '0.8em',
                      color: 'text.primary',
                      fontWeight: 'bold',
                    }}
                  >
                    (Note: {registrations.note || '---'})
                  </div> */}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ColorChip label={registrations.subsubtype.name} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Intl.NumberFormat('vi-VN', {
                      style: 'currency',
                      currency: 'VND',
                      maximumFractionDigits: 0,
                    }).format(
                      registrations.price +
                        (registrations.priceForChild || 0) -
                        (registrations.discountPrice || 0) >
                        0
                        ? registrations.price +
                            (registrations.priceForChild || 0) -
                            (registrations.discountPrice || 0)
                        : 0,
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Intl.NumberFormat('vi-VN', {
                      style: 'currency',
                      currency: 'VND',
                      maximumFractionDigits: 0,
                    }).format(registrations.discountPrice || 0)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {registrations.subsubtype.serviceFor === 'GROUP'
                      ? (registrations.amount || 0) +
                        (registrations.subAmount || 0)
                      : registrations.amount || 0}
                  </TableCell>
                  {/* <TableCell>
                  <div>
                    {new Date(registrations.sos).toLocaleDateString()} -{' '}
                    {new Date(registrations.eos).toLocaleDateString()}
                  </div>
                  <div></div>
                  <div>{registrations.duration || '---'} day(s)</div>
                </TableCell> */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>
                      {new Date(registrations.sos).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>
                      {new Date(registrations.eos).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>
                      {registrations.paymentMethod
                        ?.toLowerCase()
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                    </div>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div
                      style={{
                        color: registrations.isInvoiced ? '#4caf50' : '#f44336',
                        fontWeight: 'bold',
                      }}
                    >
                      {registrations.isInvoiced
                        ? t('registration.invoiced')
                        : t('registration.notInvoiced')}
                    </div>
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      right: -14,
                      zIndex: '1 !important',
                      // Sticky cell must be opaque so scrolled columns don't bleed
                      // through it on tinted (anonymous/selected) rows.
                      backgroundColor:
                        highlightedRow === `data_${registrations.id}`
                          ? selectedRowBgOpaque
                          : isAnonymous
                            ? walkInRowBgOpaque
                            : 'background.paper',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        backgroundColor:
                          highlightedRow === `data_${registrations.id}`
                            ? selectedRowBgOpaque
                            : isAnonymous
                              ? walkInRowBgOpaque
                              : 'background.paper',
                        borderLeft: 1,
                        borderColor: 'divider',
                        px: 1,
                      }}
                    >
                      {/* <Tooltip title="View registration">
                      <IconButton
                        color="primary"
                        aria-label="view registration"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip> */}
                      <Tooltip title={t('registration.editRegistration')}>
                        <IconButton
                          color="primary"
                          aria-label="edit registration"
                          onClick={() => {
                            setSelectedRegistration(registrations);
                            setOpenRegistrationInfoDialog(true);
                          }}
                          disabled={
                            registrations.isInvoiced ||
                            selectedRegistrations.length > 0
                          }
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('registration.createInvoice')}>
                        <div>
                          <IconButton
                            disabled={
                              registrations.isInvoiced ||
                              (selectedRegistrations.length > 0 &&
                                selectedRegistrations[0] !== registrations.id)
                            }
                            color="warning"
                            aria-label="create invoice"
                            onClick={() => {
                              setSelectedRegistration(registrations);
                              setConfirmInvoiceDialogOpen(true);
                            }}
                          >
                            <Receipt />
                          </IconButton>
                        </div>
                      </Tooltip>
                      <Tooltip
                        title={
                          isAnonymous
                            ? t('registration.notAvailableForAnonymous')
                            : t('registration.sendConfirmation')
                        }
                      >
                        <span>
                          <IconButton
                            color="primary"
                            aria-label="send registration confirmation"
                            disabled={registrations.deletionMark || isAnonymous}
                            onClick={() =>
                              notifyAPIClient(
                                t('registration.sendConfirmationDev'),
                              )
                            }
                          >
                            <Send size={20} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          isAnonymous
                            ? t('registration.notAvailableForAnonymous')
                            : downloadedConfirmations.has(registrations.id)
                              ? t('registration.confirmationDownloadedAgain')
                              : t('registration.downloadConfirmation')
                        }
                      >
                        <span>
                          <IconButton
                            color={
                              downloadedConfirmations.has(registrations.id)
                                ? 'success'
                                : 'primary'
                            }
                            aria-label="download registration confirmation"
                            disabled={registrations.deletionMark || isAnonymous}
                            onClick={() =>
                              openConfirmationDialog(registrations, 'download')
                            }
                          >
                            <Download size={20} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('registration.deleteRegistration')}>
                        <IconButton
                          color="error"
                          aria-label="delete registration"
                          disabled={
                            registrations.isInvoiced ||
                            selectedRegistrations.length > 0
                          }
                          onClick={() => {
                            setSelectedRegistration(registrations);
                            setOpenDeleteRegistrationDialog(true);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
                {registrations.length === 0 ? (
                  <TableNoData />
                ) : (
                  <TableSpacerRow rows={emptyRows} />
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Other data information */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pl={2}
      >
        <Typography variant="body2" color="textSecondary">
          {t('registration.lastSync')}:{' '}
          {lastSyncTime?.format('DD/MM/YYYY - HH:mm:ss') ||
            t('registration.never')}
        </Typography>

        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1}
          onPageChange={handleChangePage}
          rowsPerPage={limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        />
      </Box>

      {/* Dialog */}
      <EditRegistrationDialog
        open={openRegistrationInfoDialog}
        handleClose={handleCloseRegistrationInfoDialog}
        registration={selectedRegistration}
        fetchRegistrations={fetchRegistrations}
      />

      <RegistrationConfirmationDialog
        open={confirmationDialogOpen}
        registrationId={selectedRegistration?.id ?? null}
        mode={confirmationMode}
        handleClose={closeConfirmationDialog}
        onDownloaded={markConfirmationDownloaded}
      />

      <Dialog
        fullWidth
        open={confirmInvoiceDialogOpen}
        onClose={() => setConfirmInvoiceDialogOpen(false)}
      >
        <DialogTitle>
          {selectedRegistrations.length > 0 && selectedUserId
            ? t('registration.createInvoicesForItems', {
                count: selectedRegistrations.length,
              })
            : t('registration.createInvoiceTitle')}
        </DialogTitle>
        <DialogContent>
          <p>
            {selectedRegistrations.length > 0 && selectedUserId
              ? t('registration.confirmCreateInvoiceMultiple')
              : t('registration.confirmCreateInvoiceSingle')}
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmInvoiceDialogOpen(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (selectedRegistrations.length > 0) {
                handlePrintMultipleInvoices();
                setConfirmInvoiceDialogOpen(false);
                return;
              }

              if (selectedRegistration) {
                handlePrintInvoice(selectedRegistration);
                setConfirmInvoiceDialogOpen(false);
              }
            }}
            loading={createInvoiceLoading}
          >
            {t('registration.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Delete */}
      <Dialog
        fullWidth
        open={openDeleteRegistrationDialog}
        onClose={() => setOpenDeleteRegistrationDialog(false)}
      >
        <DialogTitle>{t('registration.deleteRegistrationTitle')}</DialogTitle>
        <DialogContent>
          {t('registration.confirmDeleteRegistration')}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteRegistrationDialog(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (selectedRegistration) {
                handleDeleteRegistration(selectedRegistration);
                setOpenDeleteRegistrationDialog(false);
              }
            }}
          >
            {t('registration.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Scroll-to-top: the page content scrolls inside the <main> element
          (.app-page-bg has overflow-auto), so scroll that, not the window. */}
      <Tooltip title={t('registration.backToTop')}>
        <Fab
          color="primary"
          size="medium"
          aria-label="Back to top"
          onClick={() => {
            document
              .querySelector('.app-page-bg')
              ?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1200 }}
        >
          <KeyboardArrowUp />
        </Fab>
      </Tooltip>
    </Paper>
  );
};

export default RegistrationTable;
